import { useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const FORM_ENDPOINT = "https://readdy.ai/api/form/db73numn6nb9f68e98v0";

const features = [
  { icon: "ri-film-line", title: "海量影视内容", desc: "覆盖电影、电视剧、综艺等多种类型，持续更新，满足不同口味的观影需求。" },
  { icon: "ri-search-eye-line", title: "便捷检索体验", desc: "支持关键词模糊搜索与多维度筛选，帮你快速定位想看的影视作品。" },
  { icon: "ri-award-line", title: "编辑精选推荐", desc: "由编辑团队结合口碑与热度精心策划，让每一次打开都有惊喜。" },
  { icon: "ri-device-line", title: "多端流畅适配", desc: "桌面与移动端自适应设计，随手打开即可畅快浏览影视内容。" },
];

const contacts = [
  { icon: "ri-mail-line", label: "邮箱", value: "hello@moguvideo.com" },
  { icon: "ri-wechat-line", label: "微信公众号", value: "蘑菇视频官方" },
  { icon: "ri-weibo-line", label: "微博", value: "@蘑菇视频" },
  { icon: "ri-customer-service-2-line", label: "客服时间", value: "工作日 09:00 - 18:00" },
];

export default function About() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useDocumentMeta({
    title: "关于我们 - 蘑菇视频高清影视资源平台",
    description: "了解蘑菇视频的定位与特色：一个专注电影、电视剧、综艺等高清影视内容展示与推荐的在线平台，欢迎联系我们提出宝贵建议。",
    keywords: "关于蘑菇视频,蘑菇视频介绍,影视平台,联系我们,用户反馈",
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") return;
      if (typeof value === "string" && value.trim() === "") return;
      payload.append(key, value as string);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请检查网络后重试。");
    }
  };

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-background-100">
        <div className="w-full px-4 md:px-8 py-6 md:py-8">
          <Breadcrumb items={[{ label: "关于我们" }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">关于蘑菇视频</h1>
          <p className="mt-2 max-w-2xl text-sm text-foreground-600">
            一个专注高清影视内容展示与推荐的在线平台，陪伴每一位影视爱好者发现好作品。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        <section className="max-w-4xl" aria-label="网站定位与介绍">
          <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950">
            <span className="h-5 w-1 rounded-full bg-primary-500"></span>
            网站定位与介绍
          </h2>
          <div className="mt-4 space-y-4 text-sm leading-loose text-foreground-600">
            <p>
              蘑菇视频是一个面向广大影视爱好者的综合影视内容平台，主题围绕丰富多样的电影、电视剧与综艺节目展开，致力于为用户提供清晰、便捷的在线观看与资讯服务体验。
            </p>
            <p>
              我们把内容展示与导航放在首位：从首页的焦点推荐，到分类频道的多维度筛选，再到详情页的完整信息与相关推荐，我们希望每一次浏览都顺畅自然，让你在有限的时间里快速找到真正想看的内容。
            </p>
            <p>
              同时，蘑菇视频通过编辑精选榜单与影视资讯快讯，为观众提供有价值的观影参考。无论是追逐热播剧集，还是寻找一部值得回味的电影，你都能在这里获得灵感。
            </p>
          </div>
        </section>

        <section className="mt-10" aria-label="平台特色">
          <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950">
            <span className="h-5 w-1 rounded-full bg-accent-500"></span>
            平台特色
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <div key={f.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-50 text-primary-600 text-xl">
                  <i className={f.icon} aria-hidden="true"></i>
                </span>
                <h3 className="mt-3 text-sm font-semibold text-foreground-950">{f.title}</h3>
                <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-5">
          <section className="lg:col-span-2" aria-label="联系方式">
            <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950">
              <span className="h-5 w-1 rounded-full bg-primary-500"></span>
              联系方式
            </h2>
            <div className="mt-4 space-y-3">
              {contacts.map((c) => (
                <div key={c.label} className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700 text-lg">
                    <i className={c.icon} aria-hidden="true"></i>
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs text-foreground-500">{c.label}</p>
                    <p className="truncate text-sm font-medium text-foreground-900">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs leading-relaxed text-foreground-400">
              以上联系方式为演示信息，仅用于展示平台结构，请勿用于真实沟通。
            </p>
          </section>

          <section className="lg:col-span-3" aria-label="用户反馈">
            <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950">
              <span className="h-5 w-1 rounded-full bg-primary-500"></span>
              用户反馈
            </h2>
            <p className="mt-2 text-sm text-foreground-500">
              如果你对蘑菇视频有任何建议或想法，欢迎通过下方表单告诉我们，我们会认真阅读每一条反馈。
            </p>

            {status === "success" ? (
              <div className="mt-4 flex flex-col items-center justify-center rounded-lg border border-primary-200 bg-primary-50 py-12 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-500 text-background-50 text-2xl">
                  <i className="ri-check-line" aria-hidden="true"></i>
                </span>
                <p className="mt-4 text-base font-semibold text-foreground-950">提交成功，感谢反馈！</p>
                <p className="mt-1 text-sm text-foreground-600">你的建议我们已经收到，会作为后续优化的参考。</p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-5 flex items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100 cursor-pointer"
                >
                  <i className="ri-edit-line" aria-hidden="true"></i>
                  再写一条
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                id="feedback-form"
                className="mt-4 space-y-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="fb-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      称呼 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="fb-name"
                      name="name"
                      type="text"
                      required
                      placeholder="怎么称呼你？"
                      className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="fb-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="fb-email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="fb-subject" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    反馈类型
                  </label>
                  <select
                    id="fb-subject"
                    name="subject"
                    defaultValue="内容建议"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                  >
                    <option value="内容建议">内容建议</option>
                    <option value="播放问题">播放问题</option>
                    <option value="功能体验">功能体验</option>
                    <option value="合作咨询">合作咨询</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="fb-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    反馈内容 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="fb-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请描述你的建议或遇到的问题（最多 500 字）"
                    className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  ></textarea>
                </div>

                <div className="contact-extra-field">
                  <label htmlFor="fb-contact-alt">备用联系方式（请勿填写）</label>
                  <input
                    id="fb-contact-alt"
                    name="contact_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                {status === "error" && formError && (
                  <p className="flex items-center gap-1.5 rounded-md bg-accent-100 px-3 py-2 text-xs text-accent-700">
                    <i className="ri-error-warning-line" aria-hidden="true"></i>
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer sm:w-auto"
                >
                  <i className="ri-send-plane-line" aria-hidden="true"></i>
                  {status === "submitting" ? "提交中…" : "提交反馈"}
                </button>
              </form>
            )}
          </section>
        </div>

        <div className="mt-10 flex flex-col items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-foreground-600">想直接开始探索？回到首页，发现更多精彩影视内容。</p>
          <Link
            to="/"
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-home-4-line" aria-hidden="true"></i>
            返回首页
          </Link>
        </div>
      </div>
    </div>
  );
}